import { IIconProps } from '@fluentui/react';
import { IAppContext } from 'App/AppContext';
import { TFunction } from 'i18next';
import { ApprovalMaxUsers } from 'models/approval';
import ResourceLink from 'models/resourceLink';
import Task, { TaskStateId, TaskTypes } from 'models/tasks/task';
import { SystemTaskTypes } from 'models/tasks/taskType';
import User from 'models/user';
import { apiGetLinkOwners, apiResolveLinkApprovers } from 'services/Api/linkService';
import { apiAddTask, apiRemoveTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { IPSharedColors } from 'themes/runtime/fluentColors';
import { INewApproval } from './CreateApproval';
import ProcessingActivity from 'models/processingActivity';

export const IconStatusApproved: IIconProps = {
  iconName: 'SkypeCircleCheck',
  styles: {
    root: {
      fontSize: 20,
      color: IPSharedColors.green20,
    },
  },
};

export const IconStatusRejected: IIconProps = {
  iconName: 'StatusErrorFull',
  styles: {
    root: {
      fontSize: 20,
      color: IPSharedColors.red20,
    },
  },
};

export const IconStatusPending: IIconProps = {
  iconName: 'CircleRing',
  styles: {
    root: {
      fontSize: 20,
      color: IPSharedColors.gray20,
    },
  },
};

export const IconStatusConcept: IIconProps = {
  iconName: 'SkypeCircleClock',
  styles: {
    root: {
      fontSize: 20,
      color: IPSharedColors.gray20,
    },
  },
};

const createNewApprovalBaseTask = (link: ResourceLink, appContext: IAppContext, t: TFunction<string[]>): Task => {
  const newTask = new Task();

  newTask.hidden = true; // Approval series are always hidden
  newTask.taskMasterId = 0; // For Master task
  newTask.taskType = TaskTypes.Event; // Generic event
  newTask.taskTypeId = appContext.globalDataCache.taskTypes.getTypeOfSystemType(SystemTaskTypes.Approval)?.taskTypeId; //specific approval event type
  newTask.taskStates = appContext.globalDataCache.taskStates.items;
  newTask.taskStateId = newTask.getFirstState();
  newTask.name = t('library:ApprovalTasks.AddContent.ApprovalName', { linkName: link.linkName });

  //assign the main task to the creator
  const currentUser = appContext.user;
  newTask.user = currentUser;
  newTask.userId = currentUser.id;
  //default owner/creator to current user
  newTask.creatorId = currentUser.id;
  newTask.creator = currentUser;
  newTask.ownerId = currentUser.id;
  newTask.owner = currentUser;

  return newTask;
};

/**
 * Builds the approval the user then reviews and edits in the panel. WHO the approvers are is not
 * decided here: the server resolves it (previous approvers or template, else the document owner or
 * the members of its owner role) so the library panel, the audit publish flow and the public API all
 * answer that question the same way.
 */
export const createNewApprovalTask = async (
  newApproval: INewApproval,
  appContext: IAppContext,
  t: TFunction<string[]>,
): Promise<Task> => {
  let newTask = createNewApprovalBaseTask(newApproval.item, appContext, t);
  newApproval.task = newTask;

  // assign Link to the main and subtasks
  newTask.resourceLinkIds = [newApproval.item.linkId];

  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  const resolved = await apiResolveLinkApprovers(newApproval.item.linkId, accessToken);
  newApproval.resolved = resolved;

  //the template of the previous round brings the customer's own form, labels and workflow
  if (resolved.templateId && appContext.globalDataCache.templates.has(resolved.templateId)) {
    const template = appContext.globalDataCache.templates.get(resolved.templateId);
    newTask = copyFromTemplate(template, newTask, appContext);
    newApproval.task = newTask;
  }

  //the resolved approvers replace whatever the template copy produced: the server already folded the
  //template assignments and the owner placeholder into this one list
  newTask.instances = [];

  for (const userId of resolved.userIds.slice(0, ApprovalMaxUsers)) {
    if (!appContext.globalDataCache.users.has(userId)) continue;
    const user = appContext.globalDataCache.users.get(userId);
    const subTask = newTask.clone();
    subTask.templateId = undefined;
    subTask.hidden = false;
    subTask.taskRelations = [];
    //a subtask has no subtasks of its own: the clone copies the approvers added so far, which
    //would travel to the server as nested instances of this one
    subTask.instances = [];
    subTask.user = user;
    subTask.userId = user.id;
    newTask.instances.push(subTask);
  }

  return newTask;
};

export const copyFromTemplate = (template: Task, task: Task, appContext: IAppContext): Task => {
  try {
    const newTask = task.clone();

    //all approval series are hidden from other task views because we manage the approvals from this interface
    //when a workflow is triggered, the back-end sets the subtasks also to hidden
    newTask.hidden = true;

    //copy the template id, this will trigger a workflow by the back-end, if any
    newTask.templateId = template.taskId;
    newTask.instances = [];

    //copy the users and task type from template, resolved through the family: a template read before
    //its form was forked still names the version it left, while the server repointed the template to
    //the head - and the head is the form this template produces today
    const form = appContext.globalDataCache.taskTypes.getHead(template.taskTypeId, template.taskTypeFamilyId);
    newTask.taskTypeId = form?.taskTypeId ?? template.taskTypeId;
    newTask.taskTypeFamilyId = form?.taskTypeFamilyId ?? template.taskTypeFamilyId;
    newTask.formVersion = (form ?? template.formVersion)?.clone();

    //copy description as instruction
    newTask.description = template.description;

    //copy tags
    newTask.tagIds = template.tagIds ? [...template.tagIds] : [];

    //create subtasks for each assigned user in the template
    if (template.instances) {
      for (let idx = 0; idx < template.instances?.length; idx++) {
        const instance = template.instances[idx];
        if (instance.isAssigned()) {
          const user = appContext.globalDataCache.users.get(instance.userId);
          const subTask = newTask.clone();
          subTask.hidden = false;
          subTask.taskRelations = [];
          subTask.instances = [];
          subTask.user = user;
          subTask.userId = user.id;
          //Do not set the template for subtasks, this prevents update/delete workflow triggers for approvals
          //This is correct because the workflows should be handled on event level, not on subtask level
          subTask.templateId = undefined;
          newTask.instances.push(subTask);
        }
      }
    }

    return newTask;
  } catch (err) {
    appContext.setError(err);

    return task;
  }
};

export const applyApprovalOwners = async (approval: INewApproval, appContext: IAppContext) => {
  try {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const ownerIds = await apiGetLinkOwners(approval.item, accessToken, appContext.globalDataCache);
    let owners = appContext.globalDataCache.users.getItemsForId(ownerIds);
    owners = owners.filter((owner) => !approval.task.instances?.some((i) => i.userId === owner.id));
    if (!approval.task.instances) approval.task.instances = [];

    for (const user of owners) {
      const subTask = approval.task.clone();
      subTask.hidden = false;
      subTask.taskRelations = [];
      subTask.instances = [];
      subTask.user = user;
      subTask.userId = user.id;
      approval.task.instances?.push(subTask);
    }
  } catch (err) {
    appContext.setError(err);
  } finally {
    return approval;
  }
};

export const createNewApprovalTaskForCurrentUser = (
  link: ResourceLink,
  appContext: IAppContext,
  t: TFunction<string[]>,
): Task => {
  const newTask = createNewApprovalBaseTask(link, appContext, t);

  // assign Link to the main and subtasks
  newTask.resourceLinkIds = [link.linkId];

  //create sub-tasks for the current user
  //based on the latest approval
  newTask.instances = [];
  const currentUser = appContext.user;

  const subTask = newTask.clone();
  subTask.templateId = undefined;
  subTask.hidden = false;
  subTask.taskRelations = [];
  subTask.user = currentUser;
  subTask.userId = currentUser.id;
  newTask.instances.push(subTask);

  return newTask;
};

export const createNewApprovalTaskForOthers = (
  link: ResourceLink,
  appContext: IAppContext,
  t: TFunction<string[]>,
): Task => {
  const newTask = createNewApprovalBaseTask(link, appContext, t);

  // assign Link to the main and subtasks
  newTask.resourceLinkIds = [link.linkId];

  return newTask;
};

export const createNewProcessingActivityApproval = (processingActivity: ProcessingActivity, template: Task, appContext: IAppContext, t: TFunction<string[]>): Task => {
  const newTask = new Task();

  newTask.hidden = true; // Approval series are always hidden
  newTask.taskMasterId = 0; // For Master task
  newTask.taskType = TaskTypes.Event;
  newTask.taskTypeId = appContext.globalDataCache.taskTypes.getTypeOfSystemType(SystemTaskTypes.ProcessingActivityApproval)?.taskTypeId; //specific approval event type
  newTask.taskStates = appContext.globalDataCache.taskStates.items;
  newTask.taskStateId = newTask.getFirstState();
  newTask.name = t('library:ApprovalTasks.AddContent.ApprovalName', { linkName: processingActivity.name });
  newTask.processingActivityIds = [processingActivity.processingActivityId];

  //assign the main task to the creator
  const currentUser = appContext.user;
  newTask.user = currentUser;
  newTask.userId = currentUser.id;
  //default owner/creator to current user
  newTask.creatorId = currentUser.id;
  newTask.creator = currentUser;
  newTask.ownerId = currentUser.id;
  newTask.owner = currentUser;

  newTask.instances = [];
  
  for (const task of template.instances ?? []) {
    const user = appContext.globalDataCache.users.get(task.userId);
    if (!user.hasLicense) continue;
    const subTask = newTask.clone();
    subTask.templateId = undefined;
    subTask.hidden = false;
    subTask.taskRelations = [];
    subTask.user = user;
    subTask.userId = user.id;

    newTask.instances.push(subTask);
  }

  return newTask;
}

/** The approvers of a round, one visible subtask per user (EditNewApprovalModal's non-template branch). */
export const setApprovalApprovers = (task: Task, users: User[]): Task => {
  task.templateId = undefined;
  task.instances = users.map((user) => {
    const subTask = task.clone();
    subTask.hidden = false;
    subTask.taskRelations = [];
    subTask.instances = [];
    subTask.user = user;
    subTask.userId = user.id;

    return subTask;
  });

  return task;
};

/** A concept is superseded, never sent in place: delete it before the new round is created. */
export const removeConceptApproval = async (link: ResourceLink, accessToken: string): Promise<void> => {
  if (link.approvals && link.approvals.length > 0) {
    const mainTask = link.approvals[0].mainTask;
    if (mainTask.taskStateId === TaskStateId.Rejected) {
      await apiRemoveTask(mainTask, accessToken);
    }
  }
};

/** Sends one prepared round: the concept goes first, then the main task with its approver subtasks. */
export const sendNewApproval = async (approval: INewApproval, appContext: IAppContext): Promise<Task> => {
  const accessToken = await appContext.getAccessToken(apiRequest.scopes);
  await removeConceptApproval(approval.item, accessToken);

  return apiAddTask(approval.task, false, accessToken, appContext.globalDataCache, undefined);
};
